import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  BookOpen, Code, Database, Brain, Layers, Eye, MessageSquare, Wrench,
  FolderOpen, Rocket, GraduationCap, DollarSign, Sparkles, Trophy,
  Users, Zap, ArrowRight, Check, Calendar, Clock, Percent, X, Send, Loader
} from 'lucide-react';

// API Base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ============= ENROLLMENT MODAL =============
const EnrollmentModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    age: '',
    education: '',
    experience: '',
    goals: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.education) {
      setStatus({ type: 'error', message: 'Please fill in all required fields' });
      return;
    }

    setStatus({ type: 'loading', message: '' });

    try {
      const response = await fetch(`${API_BASE_URL}/api/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || 'Not provided',
          interest: 'Student Program - 21-Day Course (50% Off)',
          message: `Student Program Enrollment\n\nAge: ${formData.age || 'Not specified'}\nEducation Level: ${formData.education}\nCoding Experience: ${formData.experience || 'Not specified'}\n\nGoals:\n${formData.goals || 'Not specified'}`
        }),
      });

      const data = await response.json();

      if (data.success) {
        setStatus({ type: 'success', message: 'Enrollment submitted successfully!' });
        setFormData({ name: '', email: '', phone: '', age: '', education: '', experience: '', goals: '' });
      } else {
        throw new Error(data.error || 'Failed to submit');
      }
    } catch (error) {
      console.error('Enrollment error:', error);
      setStatus({ type: 'error', message: 'Failed to submit. Please try again.' });
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (status.type === 'error') setStatus({ type: '', message: '' });
  };

  const resetModal = () => {
    setStatus({ type: '', message: '' });
    setFormData({ name: '', email: '', phone: '', age: '', education: '', experience: '', goals: '' });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative bg-slate-950 backdrop-blur-xl border border-white/[0.08] rounded-3xl p-8 max-w-md w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-emerald-500/5 rounded-3xl" />
        
        <button
          onClick={resetModal}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition z-10"
        >
          <X size={24} />
        </button>

        {status.type === 'success' ? (
          <motion.div 
            className="text-center py-8 relative z-10"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="mb-4 inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-blue-500/20 to-emerald-500/20 border border-teal-500/30">
              <Check size={32} className="text-teal-300" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">Enrollment Submitted!</h3>
            <p className="text-gray-400 mb-6">
              Welcome to the Student Program! We'll contact you within 24 hours with your login details and course access.
            </p>
            <button
              onClick={resetModal}
              className="px-6 py-2 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold hover:shadow-lg hover:shadow-teal-500/30 transition"
            >
              Close
            </button>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="relative z-10">
            <div className="mb-2 flex items-center gap-2">
              <span className="inline-flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-blue-500/20 to-emerald-500/20 border border-teal-500/30 rounded-full">
                <GraduationCap size={14} className="text-teal-400" />
                <span className="text-xs text-teal-400 font-semibold">21-Day Course</span>
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-500/20 border border-emerald-500/30 rounded-full">
                <Percent size={12} className="text-emerald-400" />
                <span className="text-xs text-emerald-400 font-semibold">50% OFF</span>
              </span>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Enroll Now</h2>
            <p className="text-teal-400 mb-6">Student Program - AI Learning Platform</p>

            {status.type === 'error' && (
              <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                {status.message}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Full Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your email"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Phone Number</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your phone number"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Age</label>
                  <select
                    value={formData.age}
                    onChange={(e) => handleChange('age', e.target.value)}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none transition"
                  >
                    <option value="" className="bg-slate-950">Select</option>
                    <option value="13-15" className="bg-slate-950">13-15</option>
                    <option value="16-18" className="bg-slate-950">16-18</option>
                    <option value="19-22" className="bg-slate-950">19-22</option>
                    <option value="23+" className="bg-slate-950">23+</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Education *</label>
                  <select
                    value={formData.education}
                    onChange={(e) => handleChange('education', e.target.value)}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none transition"
                  >
                    <option value="" className="bg-slate-950">Select</option>
                    <option value="Middle School" className="bg-slate-950">Middle School</option>
                    <option value="High School" className="bg-slate-950">High School</option>
                    <option value="Undergraduate" className="bg-slate-950">Undergraduate</option>
                    <option value="Graduate" className="bg-slate-950">Graduate</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Coding Experience</label>
                <select
                  value={formData.experience}
                  onChange={(e) => handleChange('experience', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none transition"
                >
                  <option value="" className="bg-slate-950">Select experience level</option>
                  <option value="Complete Beginner - Never coded" className="bg-slate-950">Complete Beginner - Never coded</option>
                  <option value="Some Basics - HTML/Scratch" className="bg-slate-950">Some Basics - HTML/Scratch</option>
                  <option value="Intermediate - Python/JS basics" className="bg-slate-950">Intermediate - Python/JS basics</option>
                  <option value="Advanced - Multiple languages" className="bg-slate-950">Advanced - Multiple languages</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">What do you want to build with AI?</label>
                <textarea
                  value={formData.goals}
                  onChange={(e) => handleChange('goals', e.target.value)}
                  rows="2"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition resize-none"
                  placeholder="Games, apps, chatbots, art..."
                />
              </div>

              <button
                type="submit"
                disabled={status.type === 'loading'}
                className="w-full py-3 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold hover:shadow-lg hover:shadow-teal-500/30 transition disabled:opacity-70 flex items-center justify-center gap-2"
              >
                {status.type === 'loading' ? (
                  <>
                    <Loader size={18} className="animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Start Learning
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
};

// ============= NAVBAR =============
const Navbar = ({ onEnroll }) => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.nav
      className={`fixed w-full top-0 z-50 transition-all duration-300 ${scrolled ? 'backdrop-blur-xl bg-slate-950/80 border-b border-white/5' : 'backdrop-blur-sm bg-transparent'}`}
      initial={{ y: -100 }} animate={{ y: 0 }} transition={{ duration: 0.5 }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-teal-500" />
            <span className="text-white">AI Factory</span>
          </Link>
          <div className="hidden md:flex items-center gap-12">
            <Link to="/" className="text-sm text-gray-300 hover:text-teal-400 transition">Home</Link>
            <a href="#curriculum" className="text-sm text-gray-300 hover:text-teal-400 transition">Curriculum</a>
            <a href="#benefits" className="text-sm text-gray-300 hover:text-teal-400 transition">Benefits</a>
            <a href="#journey" className="text-sm text-gray-300 hover:text-teal-400 transition">Journey</a>
          </div>
          <motion.button 
            whileHover={{ scale: 1.05 }} 
            whileTap={{ scale: 0.95 }}
            onClick={onEnroll}
            className="hidden md:block px-6 py-2.5 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-sm font-semibold text-white hover:shadow-lg hover:shadow-teal-500/50 transition">
            Enroll Now
          </motion.button>
        </div>
      </div>
    </motion.nav>
  );
};

// ============= GRADIENT ORBS =============
const GradientOrbs = () => (
  <div className="fixed inset-0 pointer-events-none overflow-hidden">
    <motion.div className="absolute -top-40 -left-40 w-80 h-80 bg-blue-600/20 rounded-full blur-3xl"
      animate={{ x: [0, 30, -30, 0], y: [0, 40, -40, 0] }} transition={{ duration: 8, repeat: Infinity }} />
    <motion.div className="absolute top-1/2 -right-40 w-80 h-80 bg-emerald-600/20 rounded-full blur-3xl"
      animate={{ x: [0, -30, 30, 0], y: [0, -40, 40, 0] }} transition={{ duration: 10, repeat: Infinity }} />
    <motion.div className="absolute -bottom-40 left-1/2 w-80 h-80 bg-teal-600/15 rounded-full blur-3xl"
      animate={{ x: [0, 40, -40, 0], y: [0, 30, -30, 0] }} transition={{ duration: 12, repeat: Infinity }} />
  </div>
);

// ============= HERO SECTION =============
const Hero = ({ onEnroll }) => {
  const stats = [
    { number: "10", label: "Learning Tracks" },
    { number: "50+", label: "Hands-On Projects" },
    { number: "24/7", label: "Learning Access" },
    { number: "Free", label: "Entry Level" }
  ];

  const highlights = [
    "Academic-friendly schedule",
    "Career guidance sessions", 
    "Student pricing (50% off)",
    "Peer learning groups",
    "Internship opportunities"
  ];

  return (
    <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden pt-20 bg-slate-950">
      <GradientOrbs />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(20,184,166,0.15),rgba(255,255,255,0))]" />
      
      <motion.div className="relative z-10 text-center max-w-5xl mx-auto px-4"
        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
        
        {/* Badge */}
        <motion.div className="mb-6 inline-flex items-center gap-3"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }}>
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 backdrop-blur-sm flex items-center gap-2">
            <GraduationCap size={14} /> FOR STUDENTS
          </span>
          <span className="text-xs font-semibold text-emerald-300 px-4 py-2 rounded-full border border-emerald-500/50 bg-emerald-500/10 backdrop-blur-sm flex items-center gap-2">
            <Percent size={14} /> 50% OFF
          </span>
        </motion.div>

        {/* Title */}
        <motion.h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-4 leading-tight tracking-tight"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
          <span className="block bg-gradient-to-r from-blue-400 via-teal-400 to-emerald-400 bg-clip-text text-transparent">
            Student Program
          </span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p className="text-xl md:text-2xl text-teal-300 mb-2 font-medium"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3 }}>
          Next-Gen AI Learning Platform
        </motion.p>

        {/* Duration Badge */}
        <motion.div className="mb-4"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.35 }}>
          <span className="inline-flex items-center gap-2 text-sm text-gray-400 bg-white/5 px-4 py-2 rounded-full border border-white/10">
            <Calendar size={16} className="text-teal-400" /> 21-Day Course
          </span>
        </motion.div>

        {/* Description */}
        <motion.p className="text-lg text-gray-400 mb-8 max-w-3xl mx-auto leading-relaxed"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4 }}>
          Master artificial intelligence and machine learning with hands-on projects, real-world applications, 
          and career-focused skills. Build your future in tech with cutting-edge AI education designed for 
          ambitious students ready to shape tomorrow's digital landscape.
        </motion.p>

        {/* Highlights */}
        <motion.div className="flex flex-wrap justify-center gap-3 mb-8"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.45 }}>
          {highlights.map((item, i) => (
            <span key={i} className="text-sm text-gray-300 bg-white/[0.03] border border-white/10 px-4 py-2 rounded-full flex items-center gap-2 hover:border-teal-500/30 transition">
              <Check size={14} className="text-emerald-400" /> {item}
            </span>
          ))}
        </motion.div>

        {/* Stats */}
        <motion.div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.5 }}>
          {stats.map((stat, index) => (
            <motion.div key={index} className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-5 hover:border-teal-500/30 transition-all duration-300"
              whileHover={{ y: -4, scale: 1.02 }}>
              <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-blue-400 via-teal-400 to-emerald-400 bg-clip-text text-transparent mb-1">{stat.number}</div>
              <div className="text-sm text-gray-400">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA Buttons */}
        <motion.div className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.6 }}>
          <motion.button 
            whileHover={{ scale: 1.05, boxShadow: "0 0 40px rgba(20, 184, 166, 0.3)" }} 
            whileTap={{ scale: 0.95 }}
            onClick={onEnroll}
            className="px-8 py-4 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold shadow-[0_0_30px_rgba(20,184,166,0.2)] transition flex items-center gap-2">
            Start Your AI Journey <ArrowRight size={18} />
          </motion.button>
          <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
            onClick={() => document.getElementById('curriculum').scrollIntoView({ behavior: 'smooth' })}
            className="px-8 py-4 border border-white/20 rounded-full text-white font-semibold hover:bg-white/10 transition backdrop-blur-sm hover:border-teal-500/40">
            View Curriculum
          </motion.button>
        </motion.div>
      </motion.div>
    </section>
  );
};

// ============= TRACK CARD =============
const TrackCard = ({ track, index }) => {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <motion.div className="relative group h-full"
      initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.08 }} viewport={{ once: true, margin: "-50px" }}
      onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-emerald-500/10 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <div className="relative bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-3xl p-6 hover:border-teal-500/30 transition-all duration-500 h-full flex flex-col overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-teal-500/50 to-transparent" />
        <div className="relative z-10 flex flex-col h-full">
          <div className="mb-3 inline-flex items-center gap-2">
            <span className="text-[10px] font-bold text-teal-400 px-2 py-1 rounded-full bg-teal-500/10 border border-teal-500/20">
              Track {String(index + 1).padStart(2, '0')}
            </span>
          </div>
          <motion.div className="mb-4 inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-emerald-500/20 border border-teal-500/20 group-hover:from-blue-500/30 group-hover:to-emerald-500/30 transition-all duration-500"
            animate={isHovered ? { rotate: [0, -10, 10, -10, 0] } : {}} transition={{ duration: 0.5 }}>
            <track.icon className="text-teal-300 group-hover:text-teal-200 transition" size={24} />
          </motion.div>
          <h3 className="text-lg font-bold text-white mb-2 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:via-teal-400 group-hover:to-emerald-400 group-hover:bg-clip-text transition-all duration-300">
            {track.title}
          </h3>
          <p className="text-gray-400 mb-4 leading-relaxed text-sm flex-grow">{track.description}</p>
          <ul className="space-y-1.5">
            {track.topics.map((topic, i) => (
              <motion.li key={i} className="text-xs text-gray-500 flex items-start gap-2"
                initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} transition={{ delay: 0.03 * i }}>
                <Check size={12} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>{topic}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </div>
    </motion.div>
  );
};

// ============= CURRICULUM SECTION =============
const CurriculumSection = () => {
  const tracks = [
    { icon: BookOpen, title: "AI Foundations", description: "Start your AI journey with essential concepts, understand how machines learn, and explore the exciting possibilities of artificial intelligence.",
      topics: ["What is AI and why it matters", "Types of AI: narrow, general, and super AI", "Real-world AI applications and case studies", "Career paths in AI and required skills"] },
    { icon: Code, title: "Python Basics", description: "Learn Python programming from scratch - the most popular language for AI development and data science projects.",
      topics: ["Python syntax and data structures", "Functions, loops, and conditional logic", "Working with libraries and packages", "Building your first Python programs"] },
    { icon: Database, title: "Data Science Core", description: "Master data analysis, visualization, and statistical thinking - essential skills for any AI practitioner.",
      topics: ["Data manipulation with Pandas", "Visualization with Matplotlib and Seaborn", "Statistical analysis and probability", "Data cleaning and preprocessing techniques"] },
    { icon: Brain, title: "Machine Learning", description: "Dive into supervised and unsupervised learning algorithms. Build predictive models and understand how machines learn from data.",
      topics: ["Regression and classification algorithms", "Decision trees and random forests", "Clustering and dimensionality reduction", "Model evaluation and optimization"] },
    { icon: Layers, title: "Deep Learning", description: "Explore neural networks, understand deep learning architectures, and build powerful AI models with frameworks like TensorFlow.",
      topics: ["Neural network fundamentals", "Convolutional Neural Networks (CNNs)", "Recurrent Neural Networks (RNNs)", "Transfer learning and model fine-tuning"] },
    { icon: Eye, title: "Computer Vision", description: "Build AI systems that can see and interpret images. Create projects like facial recognition, object detection, and image classification.",
      topics: ["Image processing fundamentals", "Object detection and recognition", "Facial analysis and emotion detection", "Real-time video processing projects"] },
    { icon: MessageSquare, title: "NLP & Language AI", description: "Master Natural Language Processing to build chatbots, sentiment analyzers, and language understanding systems.",
      topics: ["Text preprocessing and tokenization", "Sentiment analysis and classification", "Building chatbots and conversational AI", "Working with large language models"] },
    { icon: Wrench, title: "AI Tools Pro", description: "Learn to use cutting-edge AI tools like ChatGPT, Midjourney, and GitHub Copilot to supercharge your productivity and creativity.",
      topics: ["Prompt engineering mastery", "AI-assisted coding with GitHub Copilot", "Creating AI art and designs", "Automating tasks with AI tools"] },
    { icon: FolderOpen, title: "Portfolio Projects", description: "Build impressive real-world projects for your portfolio. Showcase your skills to colleges and future employers.",
      topics: ["End-to-end ML project development", "Deploying models to the web", "Creating professional documentation", "GitHub portfolio best practices"] },
    { icon: Rocket, title: "Career Launch", description: "Prepare for internships, competitions, and college applications. Learn how to position yourself for success in tech careers.",
      topics: ["Building a standout tech resume", "Preparing for AI interviews", "Participating in Kaggle competitions", "College application strategies for AI"] }
  ];

  return (
    <section id="curriculum" className="relative py-24 px-4 overflow-hidden bg-slate-950">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[300px] bg-gradient-to-b from-teal-950/20 to-transparent" />
      <div className="absolute top-40 right-0 w-[400px] h-[400px] bg-blue-500/[0.03] rounded-full blur-[120px]" />
      <div className="absolute bottom-40 left-0 w-[300px] h-[300px] bg-emerald-600/[0.03] rounded-full blur-[100px]" />
      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/10 border border-teal-500/20 text-[10px] text-teal-400 tracking-widest uppercase mb-4">
            <span className="w-1 h-1 rounded-full bg-teal-400" /> Learning Curriculum
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            AI Mastery <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-teal-400 to-emerald-400">Roadmap</span>
          </h2>
          <p className="text-gray-400 max-w-3xl mx-auto text-lg">
            Comprehensive learning path from AI fundamentals to advanced applications. Build portfolio projects, gain practical skills, and prepare for the future of technology careers.
          </p>
        </motion.div>
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-5">
          {tracks.map((track, index) => (<TrackCard key={index} track={track} index={index} />))}
        </div>
      </div>
    </section>
  );
};

// ============= BENEFITS SECTION =============
const BenefitsSection = () => {
  const benefits = [
    { icon: GraduationCap, title: "College Edge", description: "Stand out in college applications with AI projects and certifications. Demonstrate advanced technical skills that admissions officers love to see in STEM applicants." },
    { icon: DollarSign, title: "High Demand Skills", description: "AI engineers earn $120K+ starting salary. Learn skills that are in massive demand and build a career path to financial independence and exciting opportunities." },
    { icon: Sparkles, title: "Build Cool Stuff", description: "Create AI that recognizes faces, generates art, writes code, and solves real problems. Turn your ideas into reality with cutting-edge technology." },
    { icon: Trophy, title: "Competition Ready", description: "Compete in hackathons, Kaggle competitions, and science fairs. Win prizes, scholarships, and recognition while building your skills and network." },
    { icon: Users, title: "Community Learning", description: "Join thousands of student AI learners worldwide. Collaborate on projects, share knowledge, and make friends who share your passion for technology and innovation." },
    { icon: Zap, title: "Future Proof", description: "AI isn't just the future - it's the present. Learn skills that will be relevant for decades and position yourself at the forefront of technological revolution." }
  ];

  return (
    <section id="benefits" className="relative py-24 px-4 overflow-hidden bg-slate-900/50">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(20,184,166,0.05),transparent_70%)]" />
      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/10 border border-teal-500/20 text-[10px] text-teal-400 tracking-widest uppercase mb-4">
            <span className="w-1 h-1 rounded-full bg-teal-400" /> Student Benefits
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Why Learn <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-teal-400 to-emerald-400">AI Now?</span>
          </h2>
          <p className="text-gray-400 max-w-3xl mx-auto text-lg">
            AI is transforming every industry. Learning these skills now gives you a massive advantage for college admissions, internships, and building the future you want.
          </p>
        </motion.div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit, index) => (
            <motion.div key={index} className="group relative" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: index * 0.1 }} viewport={{ once: true }}>
              <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-8 hover:border-teal-500/30 transition-all duration-300 h-full">
                <motion.div className="mb-5 inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500/20 to-emerald-500/20 border border-teal-500/20 group-hover:from-blue-500/30 group-hover:to-emerald-500/30 transition-all"
                  whileHover={{ scale: 1.1, rotate: 5 }}>
                  <benefit.icon className="text-teal-300 group-hover:text-teal-200 transition" size={26} />
                </motion.div>
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:to-teal-400 transition-all">{benefit.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{benefit.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= JOURNEY SECTION =============
const JourneySection = () => {
  const stages = [
    { title: "Foundation Stage", description: "Learn AI basics, Python programming, and data science fundamentals. Build your first simple AI models and understand how machine learning works under the hood." },
    { title: "Skill Building", description: "Master machine learning algorithms, deep learning, and neural networks. Start working with real datasets and build increasingly complex AI applications." },
    { title: "Specialization Phase", description: "Dive deep into computer vision, NLP, or other AI domains. Create specialized projects that showcase your expertise in areas you're most passionate about." },
    { title: "Portfolio Launch", description: "Build impressive portfolio projects, compete in challenges, and prepare for opportunities. Showcase your work to colleges, employers, and the wider tech community." }
  ];

  return (
    <section id="journey" className="relative py-24 px-4 overflow-hidden bg-slate-950">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-teal-500/[0.03] rounded-full blur-[150px]" />
      <div className="max-w-4xl mx-auto relative z-10">
        <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/10 border border-teal-500/20 text-[10px] text-teal-400 tracking-widest uppercase mb-4">
            <span className="w-1 h-1 rounded-full bg-teal-400" /> Learning Journey
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Your Path to <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-teal-400 to-emerald-400">AI Mastery</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Clear progression from complete beginner to job-ready AI developer. Each stage builds on the previous one, ensuring solid foundations and practical expertise.
          </p>
        </motion.div>
        <div className="relative">
          <div className="absolute left-8 md:left-1/2 md:-translate-x-1/2 top-0 bottom-0 w-[2px] bg-gradient-to-b from-blue-500 via-teal-500 to-emerald-500" />
          {stages.map((stage, index) => (
            <motion.div key={index} className={`relative flex items-start gap-8 mb-12 last:mb-0 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
              initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: index * 0.1 }} viewport={{ once: true }}>
              <div className="absolute left-8 md:left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-gradient-to-r from-blue-500 to-emerald-500 border-4 border-slate-950 z-10" />
              <div className={`ml-16 md:ml-0 md:w-[calc(50%-2rem)] ${index % 2 === 0 ? 'md:pr-8 md:text-right' : 'md:pl-8'}`}>
                <motion.div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6 hover:border-teal-500/30 transition-all duration-300" whileHover={{ y: -4 }}>
                  <div className={`mb-3 ${index % 2 === 0 ? 'md:flex md:justify-end' : ''}`}>
                    <span className="text-xs font-bold text-teal-400 px-3 py-1 rounded-full bg-teal-500/10 border border-teal-500/20">Stage {String(index + 1).padStart(2, '0')}</span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{stage.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{stage.description}</p>
                </motion.div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= WHO IS THIS FOR SECTION =============
const WhoIsThisForSection = () => {
  const audiences = [
    { emoji: "🎓", title: "High School Students", desc: "Building skills for college applications" },
    { emoji: "🎒", title: "College Students", desc: "Preparing for tech internships & careers" },
    { emoji: "💻", title: "Self-Learners", desc: "Passionate about technology & AI" },
    { emoji: "🚀", title: "Future Founders", desc: "Building skills to launch startups" }
  ];

  return (
    <section className="relative py-20 px-4 overflow-hidden bg-slate-900/50">
      <div className="max-w-5xl mx-auto relative z-10">
        <motion.div className="text-center mb-12" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Perfect For Ambitious Students</h2>
          <p className="text-gray-400 max-w-2xl mx-auto">No prior experience needed - just curiosity and drive!</p>
        </motion.div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {audiences.map((audience, index) => (
            <motion.div key={index} className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6 text-center hover:border-teal-500/30 transition-all duration-300"
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: index * 0.1 }} viewport={{ once: true }} whileHover={{ y: -4 }}>
              <div className="text-4xl mb-3">{audience.emoji}</div>
              <h3 className="text-white font-semibold mb-2">{audience.title}</h3>
              <p className="text-gray-500 text-sm">{audience.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= FINAL CTA =============
const FinalCTA = ({ onEnroll }) => (
  <section className="relative py-28 px-6 overflow-hidden bg-slate-950">
    <div className="max-w-3xl mx-auto">
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-[600px] h-[300px] bg-gradient-to-r from-blue-500/10 via-teal-500/10 to-emerald-500/10 rounded-full blur-[100px]" />
      </div>
      <motion.div className="relative text-center" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
        <motion.div className="mb-6 inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500/20 to-emerald-500/20 border border-teal-500/20"
          whileHover={{ scale: 1.1, rotate: 5 }}>
          <Rocket className="text-teal-300" size={40} />
        </motion.div>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-5 tracking-tight">
          Start Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-teal-400 to-emerald-400">AI Journey</span>
        </h2>
        <p className="text-gray-400 mb-6 max-w-lg mx-auto leading-relaxed">
          No prior experience needed. Join thousands of students learning AI and building the future. Your journey starts today.
        </p>
        <div className="flex flex-wrap justify-center gap-4 mb-10">
          {["21-Day Course", "50% Student Discount", "Hands-on Projects", "Career Guidance"].map((item, i) => (
            <span key={i} className="text-sm text-gray-400 flex items-center gap-2">
              <Check size={16} className="text-emerald-400" />{item}
            </span>
          ))}
        </div>
        <motion.div className="flex flex-col sm:flex-row gap-4 justify-center" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }} viewport={{ once: true }}>
          <motion.button 
            whileHover={{ scale: 1.05, boxShadow: "0 0 40px rgba(20, 184, 166, 0.3)" }} 
            whileTap={{ scale: 0.95 }}
            onClick={onEnroll}
            className="px-10 py-4 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 text-white rounded-full font-semibold text-lg transition-all flex items-center justify-center gap-2">
            Enroll Now <ArrowRight size={20} />
          </motion.button>
          <Link to="/training">
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
              className="px-10 py-4 border border-white/20 rounded-full text-white font-semibold text-lg hover:bg-white/10 hover:border-teal-500/40 transition-all">
              View All Programs
            </motion.button>
          </Link>
        </motion.div>
      </motion.div>
    </div>
  </section>
);

// ============= FOOTER =============
const Footer = () => (
  <footer className="relative border-t border-white/5 py-12 px-4 bg-slate-950">
    <div className="max-w-7xl mx-auto">
      <div className="grid md:grid-cols-4 gap-8 mb-8">
        <div>
          <Link to="/" className="text-white font-bold mb-4 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-teal-500" /> AI Factory
          </Link>
          <p className="text-gray-400 text-sm mt-4">Empowering the next generation of AI innovators.</p>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-4 text-xs tracking-wider uppercase">Programs</h4>
          <ul className="space-y-2 text-gray-400 text-sm">
            <li><Link to="/ai-fundamentals" className="hover:text-teal-400 transition">AI Fundamentals</Link></li>
            <li><Link to="/corporate-ai-training" className="hover:text-teal-400 transition">Corporate Training</Link></li>
            <li><Link to="/ai-fellowship" className="hover:text-teal-400 transition">AI Fellowship</Link></li>
            <li><Link to="/student-program" className="hover:text-teal-400 transition">Student Program</Link></li>
            <li><Link to="/prompt-engineering" className="hover:text-teal-400 transition">Prompt Engineering</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-4 text-xs tracking-wider uppercase">Company</h4>
          <ul className="space-y-2 text-gray-400 text-sm">
            <li><Link to="/#about" className="hover:text-teal-400 transition">About</Link></li>
            <li><Link to="/training" className="hover:text-teal-400 transition">All Programs</Link></li>
            <li><Link to="/#contact" className="hover:text-teal-400 transition">Contact</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-4 text-xs tracking-wider uppercase">Legal</h4>
          <ul className="space-y-2 text-gray-400 text-sm">
            <li><a href="#" className="hover:text-teal-400 transition">Privacy Policy</a></li>
            <li><a href="#" className="hover:text-teal-400 transition">Terms of Service</a></li>
            <li><a href="#" className="hover:text-teal-400 transition">Refund Policy</a></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/5 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
        <p className="text-gray-600 text-sm">© 2025 AI Factory. All rights reserved.</p>
        <div className="flex gap-6">
          <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-teal-400 transition text-sm">Twitter</a>
          <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-teal-400 transition text-sm">LinkedIn</a>
          <a href="https://discord.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-teal-400 transition text-sm">Discord</a>
        </div>
      </div>
    </div>
  </footer>
);

// ============= MAIN PAGE =============
export default function StudentProgramPage() {
  const [enrollmentModal, setEnrollmentModal] = useState(false);

  const handleEnroll = () => {
    setEnrollmentModal(true);
  };

  return (
    <div className="bg-slate-950 min-h-screen overflow-x-hidden text-white">
      <Navbar onEnroll={handleEnroll} />
      <Hero onEnroll={handleEnroll} />
      <CurriculumSection />
      <BenefitsSection />
      <WhoIsThisForSection />
      <JourneySection />
      <FinalCTA onEnroll={handleEnroll} />
      <Footer />

      {/* Enrollment Modal */}
      <EnrollmentModal
        isOpen={enrollmentModal}
        onClose={() => setEnrollmentModal(false)}
      />
    </div>
  );
}